import { useCallback, useMemo, useState } from "react";
import type { DropdownSearchConfig, Extracted, NormalizedItem } from "./types";

const stripDiacritics = (value: string) => value.normalize("NFD").replace(/\p{Diacritic}/gu, "");

const normalize = (value: string, ignoreDiacritics?: boolean) => {
	const lower = value.toLowerCase();
	return ignoreDiacritics ? stripDiacritics(lower) : lower;
};

// Ordered runs of a label, flagged as matched or not — drives `HighlightedText`. The type lives with the
// (now shared) component; re-exported here so existing `./useDropdownSearch` importers stay unchanged.
import type { HighlightSegment } from "../../molecules/HighlightedText";

export type { HighlightSegment };

const substringMatch = (haystack: string, needle: string) => haystack.includes(needle);

const tokensMatch = (haystack: string, needle: string) => {
	const tokens = needle.split(/\s+/).filter(Boolean);
	return tokens.every((token) => haystack.includes(token));
};

// Subsequence ("fuzzy") match: every needle char appears in order.
const fuzzyMatch = (haystack: string, needle: string) => {
	let index = 0;
	for (const char of haystack) {
		if (char === needle[index]) index += 1;
		if (index === needle.length) return true;
	}
	return needle.length === 0;
};

/**
 * Filters the normalized rows by the current query. Pure over `normalizedItems` + `query`, so it can
 * only ever change what is *visible* — it never touches selection (the invariant).
 */
export const useDropdownSearch = <T>(normalizedItems: NormalizedItem<T>[], config: DropdownSearchConfig<T>, enabled: boolean) => {
	const [query, setQuery] = useState("");
	const { getSearchText, matchMode = "substring", highlight = true, ignoreDiacritics } = config;

	const searchTextOf = useCallback(
		(item: NormalizedItem<T>) => {
			if (getSearchText) {
				const raw = getSearchText(item.value as Extracted<T>);
				return Array.isArray(raw) ? raw.join(" ") : raw;
			}
			return item.searchLabel;
		},
		[getSearchText],
	);

	const matcher = useMemo(() => {
		if (matchMode === "tokens") return tokensMatch;
		if (matchMode === "fuzzy") return fuzzyMatch;
		return substringMatch;
	}, [matchMode]);

	const normalizedQuery = useMemo(() => normalize(query.trim(), ignoreDiacritics), [query, ignoreDiacritics]);

	const filteredItems = useMemo(() => {
		if (!enabled || !normalizedQuery) return normalizedItems;
		// Group headers always pass the filter; the panel hides any header whose children are all filtered out.
		// So a matched option keeps its group heading (search by child), and a group-name query — which matches
		// children via their searchLabel — renders the group intact (search by group).
		return normalizedItems.filter((item) => !!item.groupValues || matcher(normalize(searchTextOf(item), ignoreDiacritics), normalizedQuery));
	}, [enabled, normalizedQuery, normalizedItems, matcher, searchTextOf, ignoreDiacritics]);

	// Contiguous-substring highlight only (tokens/fuzzy don't map to a single run — leave label plain).
	const getHighlightSegments = useCallback(
		(label: string): HighlightSegment[] => {
			if (!highlight || !normalizedQuery || matchMode !== "substring") return [{ text: label, match: false }];
			const index = normalize(label, ignoreDiacritics).indexOf(normalizedQuery);
			if (index < 0) return [{ text: label, match: false }];
			return [
				{ text: label.slice(0, index), match: false },
				{ text: label.slice(index, index + normalizedQuery.length), match: true },
				{ text: label.slice(index + normalizedQuery.length), match: false },
			].filter((segment) => segment.text.length > 0);
		},
		[highlight, normalizedQuery, matchMode, ignoreDiacritics],
	);

	return { query, setQuery, filteredItems, getHighlightSegments };
};
